/**
 * Display of a calendar period with an associated period selector that allows
 * the user to change the period
 */

import Backbone from 'backbone'

import template from './period-display.hbs'
import CalendarPeriodSelector from './calendar-period-selector'
import calendars from 'scoreboard/components/calendars'
import Tooltip from 'scoreboard/components/tooltip'
import _ from 'lodash'
import globals from 'scoreboard/components/globals'
import guard from 'scoreboard/components/unsaved/guard'
import ForwardBackwardSizer from 'scoreboard/components/forward-backward-sizer'
import i18n from 'scoreboard/components/i18n'

var PeriodDisplay = Backbone.View.extend({

	tagName: 'ul',

	selectorView: CalendarPeriodSelector,

	events: {
		'click .dropdown': 'selector'
	},

	// this.options.guard indicates whether changes of the calendar period
	// should trigger an unsaved changes warning, if appropriate
	initialize: function(options) {
		this.options = options || {}
		// #8386 a null periodId indicates "All time", but we also want to
		// default to showing the current period, so check if the periodId is
		// undefined
		var periodId = _.get(options, 'periodId')
		this.periodId = _.isUndefined(periodId) ? globals.periodId : periodId
		// this.selector seems to be reserved
		this.selectorView = options && options.selectorView || this.selectorView
	},

	render: function() {
		this.$el.empty().append(template())
		this.refresh(this.periodId)

		// If period-display is part of header tabs, it can drop to the middle of the next row
		// for smaller displays. Therefore, we allow the ForwardBackwardSizer() to handle events.
		if (this.options.mobileResizeOnly) {
			this.forwardBackwardSizer = new ForwardBackwardSizer({
				select: this.$('.dropdown .text'),
				controls: this.$el,
				backward: this.$('.backward'),
				forward: this.$('.forward'),
				mobileResizeOnly: this.options.mobileResizeOnly
			}).render()
			this.forwardBackwardSizer.on('backward', () => this.previousPeriod())
			this.forwardBackwardSizer.on('forward', () => this.nextPeriod())
		} else {
			this.$('.backward').on('click', () => this.previousPeriod())
			this.$('.forward').on('click', () => this.nextPeriod())
		}

		return this
	},

	// refresh the display of the period label, and the disabled state of the
	// forward and backward buttons
	refresh: function(periodId) {
		this.periodId = periodId
		this.update(periodId)

		// update the buttons

		// figure out where the current period is
		var period = calendars.periodsById[this.periodId]
		if (period) {
			// keep track of whether to allow backward and forward
			var backward, forward
			// if displaying the first period, disable the backward button
			if (_.head(period.calendar.periods).id == period.id) {
				backward = false
			}
			// if displaying the last period, disable the forward button
			if (_.last(period.calendar.periods).id == period.id) {
				forward = false
			}
			backward = _.isUndefined(backward) ? true : backward
			forward = _.isUndefined(forward) ? true : forward
			this.$('.backward').toggleClass('is-disabled', !backward)
			this.$('.forward').toggleClass('is-disabled', !forward)
		}
	},

	// update the displayed calendar period
	update: function(periodId) {
		var period = calendars.periodsById[periodId]
		if (period) {
			if (this.options.periodDelta) {
				let text = calendars.safeRelativePeriod(this.periodId, this.options.periodDelta).label
				this.$('.dropdown a .text').text(text)
			} else {
				let briefingSpecificPeriod = this.options.briefingSpecificPeriodId ? ` (${i18n.get('common.specificPeriod')})` : ''
				this.$('.dropdown a .text').text(period.label + briefingSpecificPeriod)
			}
		}
	},

	changePeriod: function(newPeriodId) {
		if (this.periodId != newPeriodId) {
			this.trigger('change:period', newPeriodId)
		}
		this.refresh(newPeriodId)
	},

	adjustPeriod: function(amount) {
		this._guardedRun(function() {
			var period = calendars.periodsById[this.periodId]
			var index = period.calendar.periods.indexOf(period)
			var newIndex = amount + index
			if (newIndex >= 0 && newIndex < period.calendar.periods.length) {
				var newPeriod = period.calendar.periods[newIndex]
				this.changePeriod(newPeriod.id)
			}
		}.bind(this))
	},

	previousPeriod: function() {
		this.adjustPeriod(-1)
	},

	nextPeriod: function() {
		this.adjustPeriod(1)
	},

	selector: function() {
		this._guardedRun(function() {
			if (this.disabled) {
				return
			}
			if (this.selectorTooltip && !this.selectorTooltip.removed) {
				this._removeTooltip()
				return
			}

			var options = _.extend({ periodId: this.periodId }, _.omit(this.options, 'periodId'))
			if (this.relativePeriodId) {
				options.periodId = this.relativePeriodId
			}
			this.periodSelector = new this.selectorView(options)

			let attachTo = this.$('.dropdown')
			this.selectorTooltip = new Tooltip({
				content: this.periodSelector,
				attachTo,
				placement: 'below',
				arrow: 'right',
				attachToCenter: false,
				adjustment: -(attachTo.width() /2) + 11, // This shifts the tooltip arrow directly underneath the down arrow
				extraClass: 'time-selector ' + (this.options.extraTooltipClass ? this.options.extraTooltipClass : '')
			}).render()

			this.periodSelector.on('change:period', function(periodId) {
				// datasets depend on all arguments being passed along, not just
				// the periodId
				this.changePeriod.apply(this, arguments)
				this._removeTooltip()
			}.bind(this))
		}.bind(this))
	},

	_guardedRun: function(func) {
		if (this.options.guard) {
			guard.conditionallyRun(func)
		} else {
			func()
		}
	},

	_removeTooltip: function () {
		_.invokeMap([this.periodSelector, this.selectorTooltip], 'remove')
		delete this.periodSelector
		delete this.selectorTooltip
	},

	disable: function() {
		this.$('.dropdown a').addClass('is-disabled')
		this.disabled = true
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		this._removeTooltip()
		_.invokeMap([this.forwardBackwardSizer], 'remove')
	}

})

export default PeriodDisplay

PeriodDisplay.__filename__ = 'components/period-display.js'
